import React, { useState, useEffect } from 'react';
import {
  HomeIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  DocumentPlusIcon,
  ClockIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import UsuariosList from './components/UsuariosList';
import EnsayoFormBasico from './components/EnsayoFormBasico';
import Dashboard from './components/Dashboard';
import Busqueda from './components/Busqueda';
import AnalisisPendientes from './components/AnalisisPendientes';
import Configuracion from './components/Configuracion';
import MisAnalisis from './components/MisAnalisis';
import Login from './components/Login';
import CrearAnalisis from './components/CrearAnalisis';
import CargarEnsayos from './components/CargarEnsayos';
import Gestion from './components/Gestion';
import { Header } from './components/layout/Header';
import { TabNav, TabNavItem } from './components/layout/TabNav';
import { ActiveTab } from './types';
import {
  isAuthenticated,
  logout,
  getCurrentUser,
  getUserType,
  hasPermission,
  hasAnyManagementPermission,
  refreshCurrentUser,
} from './services/authService';

const App: React.FC = () => {
  const [isAuth, setIsAuth] = useState(isAuthenticated());
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const [selectedAnalisisId, setSelectedAnalisisId] = useState<string>('');
  const userType = getUserType();

  useEffect(() => {
    const authenticated = isAuthenticated();
    setIsAuth(authenticated);

    if (authenticated) {
      const u = getCurrentUser();
      setCurrentUser(u);
      if (u && u.estado_pass === 0) {
        setActiveTab('configuracion' as ActiveTab);
      }
      refreshCurrentUser().then(actualizado => {
        if (actualizado) setCurrentUser(actualizado);
      });
    }
  }, []);

  const handleLogin = (_username: string, _password: string) => {
    setIsAuth(true);
    const u = getCurrentUser();
    setCurrentUser(u);
    // La clave con la que ingresa es temporal: hasta que defina una propia sólo
    // ve la pantalla de configuración.
    if (u && u.estado_pass === 0) {
      setActiveTab('configuracion' as ActiveTab);
    } else {
      setActiveTab('dashboard');
    }
  };

  const handleLogout = () => {
    logout();
    setIsAuth(false);
    setCurrentUser(null);
    setActiveTab('dashboard');
  };

  const handleNavigate = (tab: ActiveTab, id?: string) => {
    setActiveTab(tab);
    setCurrentUser(getCurrentUser());
    if (id) {
      setSelectedAnalisisId(id);
    }
  };

  const canSearch = () => hasPermission('p_busqueda');
  const canUpload = () => hasPermission('p_carga');
  const canEdit = () => hasPermission('p_edicion');
  const canCreateAnalisis = () => hasPermission('p_nuevo_analisis');

  if (!isAuth) {
    return <Login onLogin={handleLogin} />;
  }

  const mustChangePassword = currentUser && currentUser.estado_pass === 0;
  const isClienteOLaboratorio = userType === 'cliente' || userType === 'laboratorio';
  // Un laboratorio recibe análisis, no los genera: los carga un administrador o
  // el propio cliente.
  const puedeCrearAnalisis = canCreateAnalisis() && userType !== 'laboratorio';

  const navItems: TabNavItem[] = [
    { key: 'dashboard', label: 'Panel', icon: <HomeIcon className="h-4 w-4" /> },
    ...(canSearch() || canEdit()
      ? [{ key: 'analisis-pendientes' as ActiveTab, label: 'Análisis Pendientes', icon: <ClockIcon className="h-4 w-4" /> }]
      : []),
    ...(canSearch() ? [{ key: 'busqueda' as ActiveTab, label: 'Búsqueda', icon: <MagnifyingGlassIcon className="h-4 w-4" /> }] : []),
    ...(isClienteOLaboratorio
      ? [{ key: 'mis-analisis' as ActiveTab, label: 'Análisis', icon: <ChartBarIcon className="h-4 w-4" /> }]
      : []),
    ...(puedeCrearAnalisis
      ? [{ key: 'nuevo-analisis' as ActiveTab, label: 'Nuevo Análisis', icon: <DocumentPlusIcon className="h-4 w-4" /> }]
      : []),
    { key: 'configuracion', label: 'Configuración', icon: <Cog6ToothIcon className="h-4 w-4" /> },
  ];

  // Gestión va separada porque se alinea a la derecha de la barra.
  const gestionItems: TabNavItem[] = hasAnyManagementPermission()
    ? [{ key: 'gestion', label: 'Gestión', icon: <AdjustmentsHorizontalIcon className="h-4 w-4" /> }]
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={currentUser} userType={userType} onLogout={handleLogout} />

      <div className="w-full px-4 sm:px-6 py-6">
        {!mustChangePassword && (
          <div className="mb-6 pb-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
            <TabNav items={navItems} activeTab={activeTab} onChange={setActiveTab} />
            <TabNav items={gestionItems} activeTab={activeTab} onChange={setActiveTab} align="right" />
          </div>
        )}

        {mustChangePassword ? (
          <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-card">
            <Configuracion onNavigate={handleNavigate} />
          </div>
        ) : (
          <div className={activeTab === 'dashboard' ? '' : 'bg-white p-6 rounded-lg border border-slate-200 shadow-card'}>
            {activeTab === 'dashboard' && <Dashboard onNavigate={handleNavigate} />}
            {activeTab === 'usuarios' && <UsuariosList />}
            {activeTab === 'gestion' && hasAnyManagementPermission() && <Gestion />}
            {activeTab === 'cargar-ensayos' && canUpload() && <CargarEnsayos analisisId={selectedAnalisisId} onNavigate={handleNavigate} />}
            {activeTab === 'cargar-ensayos-basico' && canUpload() && <EnsayoFormBasico />}
            {activeTab === 'busqueda' && canSearch() && <Busqueda />}
            {activeTab === 'analisis-pendientes' && (canSearch() || canEdit()) && <AnalisisPendientes />}
            {activeTab === 'mis-analisis' && isClienteOLaboratorio && <MisAnalisis />}
            {activeTab === 'configuracion' && <Configuracion onNavigate={handleNavigate} />}
            {activeTab === 'nuevo-analisis' && puedeCrearAnalisis && <CrearAnalisis onNavigate={handleNavigate} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default App;
